import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import { behaviorNameKey } from 'utils/behaviorNames';

// Purpose / reinforcement checkbox catalogues — must stay in sync with the IEP
// form (pages/inclusionhead/iep.jsx) so a behavior authored here is identical
// in shape to one authored in the IEP form.
const BEHAVIOR_PURPOSE_OPTIONS = [
  { key: 'emotionalReactivity', labelKey: 'iep.pbsp.behavior.purpose.emotionalReactivity' },
  { key: 'cognitiveDistortions', labelKey: 'iep.pbsp.behavior.purpose.cognitiveDistortions' },
  { key: 'modeling', labelKey: 'iep.pbsp.behavior.purpose.modeling' },
  { key: 'physiologicalPurpose', labelKey: 'iep.pbsp.behavior.purpose.physiologicalPurpose' },
  { key: 'communicateNeeds', labelKey: 'iep.pbsp.behavior.purpose.communicateNeeds' },
  { key: 'curriculumInstruction', labelKey: 'iep.pbsp.behavior.purpose.curriculumInstruction' }
];

const BEHAVIOR_REINFORCEMENT_OPTIONS = [
  { key: 'gainAttention', labelKey: 'iep.pbsp.behavior.reinforcement.gainAttention' },
  { key: 'gainTangible', labelKey: 'iep.pbsp.behavior.reinforcement.gainTangible' },
  { key: 'gainSensoryInput', labelKey: 'iep.pbsp.behavior.reinforcement.gainSensoryInput' },
  { key: 'fillHabitualNeed', labelKey: 'iep.pbsp.behavior.reinforcement.fillHabitualNeed' },
  { key: 'escapeTask', labelKey: 'iep.pbsp.behavior.reinforcement.escapeTask' },
  { key: 'socialAvoidance', labelKey: 'iep.pbsp.behavior.reinforcement.socialAvoidance' },
  { key: 'releaseTension', labelKey: 'iep.pbsp.behavior.reinforcement.releaseTension' },
  { key: 'escapeSensoryInput', labelKey: 'iep.pbsp.behavior.reinforcement.escapeSensoryInput' }
];

// Canonical empty behavior — mirrors blankBehavior() in the IEP form so both
// entry points write the exact same JSON structure into snapshot_data.behaviors.
export const blankBehavior = () => ({
  behaviorName: '',
  operationalDefinition: '',
  measurement: {
    trackFrequency: false,
    trackDuration: false,
    trackIntensity: false,
    notes: ''
  },
  settings: '',
  motivatingOperations: '',
  antecedents: '',
  consequences: '',
  purpose: {
    emotionalReactivity: false,
    cognitiveDistortions: false,
    modeling: false,
    physiologicalPurpose: false,
    communicateNeeds: false,
    curriculumInstruction: false,
    other: '',
    description: ''
  },
  reinforcement: {
    gainAttention: false,
    gainTangible: false,
    gainSensoryInput: false,
    fillHabitualNeed: false,
    escapeTask: false,
    socialAvoidance: false,
    releaseTension: false,
    escapeSensoryInput: false,
    other: '',
    description: ''
  }
});

// Deep-merge an existing (possibly partial/legacy) behavior onto the blank
// defaults so nested group objects always exist while editing.
const normalizeBehavior = (b) => {
  const base = blankBehavior();
  if (!b || typeof b !== 'object') return base;
  return {
    ...base,
    ...b,
    measurement: { ...base.measurement, ...(b.measurement || {}) },
    purpose: { ...base.purpose, ...(b.purpose || {}) },
    reinforcement: { ...base.reinforcement, ...(b.reinforcement || {}) }
  };
};

/**
 * Add/Edit dialog for a single "behavior of concern". Emits a fully-shaped
 * behavior object via onSave; the parent is responsible for persisting it into
 * the IEP version snapshot. Field set and data shape intentionally match the
 * IEP form's PBSP → Behaviors of Concern section exactly.
 */
const BehaviorEditorDialog = ({ open, initial = null, saving = false, readOnly = false, existingNames = [], onClose, onSave }) => {
  const theme = useTheme();
  const intl = useIntl();
  const isEdit = Boolean(initial);

  const [draft, setDraft] = useState(blankBehavior());
  const [nameError, setNameError] = useState(false);

  // Re-seed the draft each time the dialog opens (add → blank, edit → clone).
  useEffect(() => {
    if (open) {
      setDraft(normalizeBehavior(initial));
      setNameError(false);
    }
  }, [open, initial]);

  // Live duplicate check: the name clashes with another of the child's behaviours
  // (case- and whitespace-insensitive). `existingNames` excludes the one being edited.
  const duplicateOf = !readOnly ? existingNames.find((n) => behaviorNameKey(n) === behaviorNameKey(draft.behaviorName)) || null : null;
  const isDuplicate = Boolean(behaviorNameKey(draft.behaviorName)) && Boolean(duplicateOf);

  const setFlat = (key, val) => setDraft((prev) => ({ ...prev, [key]: val }));
  const setGroup = (groupKey, fieldKey, val) =>
    setDraft((prev) => ({ ...prev, [groupKey]: { ...(prev[groupKey] || {}), [fieldKey]: val } }));

  const handleSave = () => {
    const name = (draft.behaviorName || '').trim();
    if (!name) {
      setNameError(true);
      return;
    }
    if (isDuplicate) return;
    onSave({ ...draft, behaviorName: name.replace(/\s+/g, ' ') });
  };

  const title = readOnly
    ? intl.formatMessage({ id: 'behaviorsTab.behaviorDetails' })
    : isEdit
      ? intl.formatMessage({ id: 'behaviorsTab.editBehavior' })
      : intl.formatMessage({ id: 'behaviorsTab.addNewBehavior' });

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {/* A disabled fieldset makes every control inside non-interactive in the
            read-only (view) mode without duplicating the whole field set. */}
        <Box component="fieldset" disabled={readOnly} sx={{ border: 'none', p: 0, m: 0, minInlineSize: 0 }}>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            {/* Name */}
            <TextField
              fullWidth
              size="small"
              required={!readOnly}
              autoFocus={!readOnly}
              label={intl.formatMessage({ id: 'iep.pbsp.behavior.name' })}
              value={draft.behaviorName || ''}
              onChange={(e) => {
                setFlat('behaviorName', e.target.value);
                if (nameError) setNameError(false);
              }}
              error={nameError || isDuplicate}
              helperText={
                nameError
                  ? intl.formatMessage({ id: 'validation.required' })
                  : isDuplicate
                    ? intl.formatMessage(
                        {
                          id: 'behaviorsTab.duplicateName',
                          defaultMessage: '"{name}" already exists for this child. Use a different name or edit the existing behavior.'
                        },
                        { name: duplicateOf }
                      )
                    : undefined
              }
            />

            {/* Description block */}
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                {intl.formatMessage({ id: 'iep.pbsp.behavior.description' })}
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label={intl.formatMessage({ id: 'iep.pbsp.behavior.operationalDefinition' })}
                  value={draft.operationalDefinition || ''}
                  onChange={(e) => setFlat('operationalDefinition', e.target.value)}
                />

                {/* Measurement dimensions + notes */}
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {intl.formatMessage({ id: 'iep.pbsp.behavior.measurementHint' })}
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(draft.measurement?.trackFrequency)}
                          onChange={(e) => setGroup('measurement', 'trackFrequency', e.target.checked)}
                        />
                      }
                      label={intl.formatMessage({ id: 'iep.pbsp.behavior.frequency' })}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(draft.measurement?.trackDuration)}
                          onChange={(e) => setGroup('measurement', 'trackDuration', e.target.checked)}
                        />
                      }
                      label={intl.formatMessage({ id: 'iep.pbsp.behavior.duration' })}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(draft.measurement?.trackIntensity)}
                          onChange={(e) => setGroup('measurement', 'trackIntensity', e.target.checked)}
                        />
                      }
                      label={intl.formatMessage({ id: 'iep.pbsp.behavior.intensity' })}
                    />
                  </Stack>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    placeholder={intl.formatMessage({ id: 'iep.pbsp.behavior.measurementNotesPlaceholder' })}
                    value={draft.measurement?.notes || ''}
                    onChange={(e) => setGroup('measurement', 'notes', e.target.value)}
                    sx={{ mt: 1 }}
                  />
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  label={intl.formatMessage({ id: 'iep.pbsp.behavior.settings' })}
                  value={draft.settings || ''}
                  onChange={(e) => setFlat('settings', e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label={intl.formatMessage({ id: 'iep.pbsp.behavior.motivatingOperations' })}
                  helperText={intl.formatMessage({ id: 'iep.pbsp.behavior.motivatingOperationsHint' })}
                  value={draft.motivatingOperations || ''}
                  onChange={(e) => setFlat('motivatingOperations', e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  label={intl.formatMessage({ id: 'iep.pbsp.behavior.antecedents' })}
                  value={draft.antecedents || ''}
                  onChange={(e) => setFlat('antecedents', e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label={intl.formatMessage({ id: 'iep.pbsp.behavior.consequences' })}
                  value={draft.consequences || ''}
                  onChange={(e) => setFlat('consequences', e.target.value)}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Function block — Hypothesized purpose */}
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                {intl.formatMessage({ id: 'iep.pbsp.behavior.function' })}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, mb: 0.5 }}>
                {intl.formatMessage({ id: 'iep.pbsp.behavior.hypothesizedPurpose' })}
              </Typography>
              <Grid container>
                {BEHAVIOR_PURPOSE_OPTIONS.map((opt) => (
                  <Grid key={opt.key} size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(draft.purpose?.[opt.key])}
                          onChange={(e) => setGroup('purpose', opt.key, e.target.checked)}
                        />
                      }
                      label={intl.formatMessage({ id: opt.labelKey })}
                    />
                  </Grid>
                ))}
              </Grid>
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: 'iep.pbsp.behavior.other' })}
                value={draft.purpose?.other || ''}
                onChange={(e) => setGroup('purpose', 'other', e.target.value)}
                sx={{ mt: 0.5 }}
              />
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label={intl.formatMessage({ id: 'iep.pbsp.behavior.purposeDescription' })}
                value={draft.purpose?.description || ''}
                onChange={(e) => setGroup('purpose', 'description', e.target.value)}
                sx={{ mt: 1.5 }}
              />
            </Box>

            {/* Function block — Type of reinforcement maintaining the behavior */}
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {intl.formatMessage({ id: 'iep.pbsp.behavior.reinforcementTitle' })}
              </Typography>
              <Grid container>
                {BEHAVIOR_REINFORCEMENT_OPTIONS.map((opt) => (
                  <Grid key={opt.key} size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(draft.reinforcement?.[opt.key])}
                          onChange={(e) => setGroup('reinforcement', opt.key, e.target.checked)}
                        />
                      }
                      label={intl.formatMessage({ id: opt.labelKey })}
                    />
                  </Grid>
                ))}
              </Grid>
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: 'iep.pbsp.behavior.other' })}
                value={draft.reinforcement?.other || ''}
                onChange={(e) => setGroup('reinforcement', 'other', e.target.value)}
                sx={{ mt: 0.5 }}
              />
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label={intl.formatMessage({ id: 'iep.pbsp.behavior.reinforcementDescription' })}
                value={draft.reinforcement?.description || ''}
                onChange={(e) => setGroup('reinforcement', 'description', e.target.value)}
                sx={{ mt: 1.5 }}
              />
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {readOnly ? (
          <Button onClick={onClose} variant="contained">
            {intl.formatMessage({ id: 'common.close' })}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} color="inherit" disabled={saving}>
              {intl.formatMessage({ id: 'common.cancel' })}
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || isDuplicate}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isEdit ? intl.formatMessage({ id: 'behaviorsTab.saveChanges' }) : intl.formatMessage({ id: 'behaviorsTab.addBehavior' })}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

BehaviorEditorDialog.propTypes = {
  open: PropTypes.bool,
  // The behavior being edited, or null when adding a new one.
  initial: PropTypes.object,
  saving: PropTypes.bool,
  // View-only mode: fields disabled, footer shows a single Close button.
  readOnly: PropTypes.bool,
  // Names of the child's OTHER behaviours (not the one being edited): a name that
  // matches one of them (ignoring case and spaces) is refused as a duplicate.
  existingNames: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default BehaviorEditorDialog;
